/**
 * Slips Page
 * 
 * หน้ารายการสลิป - ดูประวัติการอัปโหลดและตรวจสอบสลิป
 */

import { useState, useEffect, useCallback } from 'react'
import {
  Search,
  Upload,
  RefreshCw,
  FileImage,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  Copy,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Eye,
  Building2,
} from 'lucide-react'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/components/auth/auth-provider'
import { BankLogo } from '@/components/ui/bank-logo'
import { SlipLogService, type SlipLog, type SlipLogQueryParams } from '@/domain/slip-log'
import { UploadSlipDialog } from './components/upload-slip-dialog'
import { SlipDetailDialog } from './components/slip-detail-dialog'
import { cn } from '@/lib/utils'

// ============================================
// Constants
// ============================================

const PAGE_SIZES = [10, 25, 50, 100]

// ============================================
// Helper Functions
// ============================================

function formatDateTime(dateStr?: string): string {
  if (!dateStr) return '-'
  
  try {
    const date = new Date(dateStr)
    return date.toLocaleString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    return dateStr
  }
}

function formatCurrency(amount: number): string {
  return amount.toLocaleString('th-TH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

// ============================================
// Status Badge Component
// ============================================

function StatusBadge({ status, verificationStatus }: { 
  status?: string
  verificationStatus?: string 
}) {
  // Verification status takes priority
  if (verificationStatus === 'success') {
    return (
      <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 gap-1">
        <CheckCircle2 className="w-3 h-3" />
        สำเร็จ
      </Badge>
    )
  }
  
  if (verificationStatus === 'failed') {
    return (
      <Badge className="bg-rose-500/15 text-rose-600 border-rose-500/30 gap-1">
        <XCircle className="w-3 h-3" />
        ไม่สำเร็จ
      </Badge>
    )
  }
  
  if (verificationStatus === 'error') {
    return (
      <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30 gap-1">
        <AlertCircle className="w-3 h-3" />
        ผิดพลาด
      </Badge>
    )
  }
  
  // Fallback to status
  if (status === 'Approved') {
    return (
      <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 gap-1">
        <CheckCircle2 className="w-3 h-3" />
        อนุมัติ
      </Badge>
    )
  }
  
  if (status === 'Rejected') {
    return (
      <Badge className="bg-rose-500/15 text-rose-600 border-rose-500/30 gap-1">
        <XCircle className="w-3 h-3" />
        ปฏิเสธ
      </Badge>
    )
  }
  
  return (
    <Badge className="bg-zinc-500/15 text-zinc-600 border-zinc-500/30 gap-1">
      <Clock className="w-3 h-3" />
      รอดำเนินการ
    </Badge>
  )
}

// ============================================
// Provider Badge Component
// ============================================

function ProviderBadge({ provider, uploadFrom }: { 
  provider?: string
  uploadFrom?: string 
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {uploadFrom === 'Panel' ? (
        <Badge variant="outline" className="bg-sky-500/10 text-sky-600 border-sky-500/30 text-xs px-1.5">
          Panel
        </Badge>
      ) : (
        <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30 text-xs px-1.5">
          Member
        </Badge>
      )}
      
      {provider === 'easyslip' && (
        <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/30 text-xs px-1.5">
          ES
        </Badge>
      )}
      {provider === 'slip2go' && (
        <Badge variant="outline" className="bg-orange-500/10 text-orange-600 border-orange-500/30 text-xs px-1.5">
          S2G
        </Badge>
      )}
    </div>
  )
}

// ============================================
// Duplicate Badge Component
// ============================================

function DuplicateBadge({ slipCount, slipNew }: { 
  slipCount?: number
  slipNew?: boolean 
}) {
  if (slipCount === 1 && slipNew) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
          </TooltipTrigger>
          <TooltipContent>
            <p>สลิปไม่ซ้ำ</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <XCircle className="w-5 h-5 text-rose-500" />
        </TooltipTrigger>
        <TooltipContent>
          <p>สลิปซ้ำ ({slipCount || 0} ครั้ง)</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

// ============================================
// Stats Cards Component
// ============================================

function StatsCards({ 
  total, 
  approved, 
  pending, 
  rejected: _rejected,
  totalAmount,
  isLoading 
}: {
  total: number
  approved: number
  pending: number
  rejected: number
  totalAmount: number
  isLoading: boolean
}) {
  const stats = [
    {
      label: 'สลิปทั้งหมด',
      value: total,
      icon: FileImage,
      color: 'text-blue-600',
      bg: 'bg-blue-500/10',
    },
    {
      label: 'อนุมัติแล้ว',
      value: approved,
      icon: CheckCircle2,
      color: 'text-emerald-600',
      bg: 'bg-emerald-500/10',
    },
    {
      label: 'รอดำเนินการ',
      value: pending,
      icon: Clock,
      color: 'text-amber-600',
      bg: 'bg-amber-500/10',
    },
    {
      label: 'ยอดรวม',
      value: formatCurrency(totalAmount),
      icon: Building2,
      color: 'text-violet-600',
      bg: 'bg-violet-500/10',
      isAmount: true,
    },
  ]
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {stats.map((stat) => (
        <Card key={stat.label} className="border-zinc-200/50 dark:border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={cn("p-2.5 rounded-xl", stat.bg)}>
                <stat.icon className={cn("w-5 h-5", stat.color)} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground truncate">{stat.label}</p>
                {isLoading ? (
                  <Skeleton className="h-6 w-16 mt-1" />
                ) : (
                  <p className={cn("text-xl font-bold", stat.color)}>
                    {stat.isAmount ? `฿${stat.value}` : stat.value.toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

// ============================================
// Main Component
// ============================================

export function SlipsPage() {
  const { sessionToken } = useAuth()
  
  // State
  const [slips, setSlips] = useState<SlipLog[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [uploadFromFilter, setUploadFromFilter] = useState<string>('all')
  
  // Dialogs
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)
  const [selectedSlip, setSelectedSlip] = useState<SlipLog | null>(null)
  
  // Stats
  const [stats, setStats] = useState({
    approved: 0,
    pending: 0,
    rejected: 0,
    totalAmount: 0,
  })
  
  // Load data
  const loadSlips = useCallback(async (showLoading = true) => {
    if (!sessionToken) return
    
    if (showLoading) setIsLoading(true)
    else setIsRefreshing(true)
    
    try {
      const params: SlipLogQueryParams = {
        page,
        limit: pageSize,
        search: search || undefined,
        status: statusFilter !== 'all' ? (statusFilter as any) : undefined,
        uploadFrom: uploadFromFilter !== 'all' ? (uploadFromFilter as any) : undefined,
      }
      
      const result = await SlipLogService.getAll(sessionToken, params)
      
      setSlips(result.results)
      setTotalCount(result.count)
      
      // Calculate stats from all results (could be improved with separate API)
      const calcStats = SlipLogService.calculateStats(result.results)
      setStats({
        approved: calcStats.approvedSlips,
        pending: calcStats.pendingSlips,
        rejected: calcStats.rejectedSlips,
        totalAmount: calcStats.totalAmount,
      })
    } catch (error) {
      console.error('Failed to load slips:', error)
    } finally {
      setIsLoading(false)
      setIsRefreshing(false)
    }
  }, [sessionToken, page, pageSize, search, statusFilter, uploadFromFilter])
  
  useEffect(() => {
    loadSlips()
  }, [loadSlips])
  
  // Handlers
  const handleRefresh = () => loadSlips(false)
  
  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }
  
  const handleViewDetail = (slip: SlipLog) => {
    setSelectedSlip(slip)
    setDetailDialogOpen(true)
  }
  
  const handleUploadSuccess = () => {
    loadSlips(false)
  }
  
  const totalPages = Math.ceil(totalCount / pageSize)

  // Get slip image URL - use runtime config for base URL
  const getSlipImageUrl = (imagePath?: string) => {
    if (!imagePath) return null

    // Cloud storage (Spaces/S3): already a full URL
    if (imagePath.startsWith('http')) return imagePath

    // Normalize old format to new location
    // Old format: "uploads/1771420494750.jpg" → "uploads/slips-old/1771420494750.jpg"
    // New format: "uploads/slips/slip-2026-02-18-xxx.jpg" → keep as-is
    let normalizedPath = imagePath

    if (normalizedPath.startsWith('uploads/') && !normalizedPath.includes('slips/')) {
      // Old format detected - map to slips-old directory
      normalizedPath = normalizedPath.replace('uploads/', 'uploads/slips-old/')
    }

    // Try runtime config first (supports server switching without rebuild)
    const runtimeConfig = (window as any).APP_CONFIG
    if (runtimeConfig?.PARSE_V2_SERVER_URL) {
      // Remove /api/v1 suffix to get base URL
      const baseUrl = runtimeConfig.PARSE_V2_SERVER_URL.replace(/\/api\/v1$/, '')
      return `${baseUrl}/${normalizedPath}`
    }

    // Fallback to build-time env var
    const buildTimeUrl = import.meta.env.VITE_FRONT_API_URL || import.meta.env.VITE_PARSE_V2_SERVER_URL?.replace(/\/api\/v[12]$/, '') || ''
    return `${buildTimeUrl}/${normalizedPath}`
  }
  
  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">รายการสลิป</h1>
          <p className="text-muted-foreground mt-1">
            ตรวจสอบและจัดการสลิปการโอนเงิน
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="gap-2"
          >
            <RefreshCw className={cn("w-4 h-4", isRefreshing && "animate-spin")} />
            รีเฟรช
          </Button>
          
          <Button
            size="sm"
            onClick={() => setUploadDialogOpen(true)}
            className="gap-2 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700"
          >
            <Upload className="w-4 h-4" />
            อัปโหลดสลิป
          </Button>
        </div>
      </div>
      
      {/* Stats */}
      <StatsCards
        total={totalCount}
        approved={stats.approved}
        pending={stats.pending}
        rejected={stats.rejected}
        totalAmount={stats.totalAmount}
        isLoading={isLoading}
      />
      
      {/* Filters */}
      <Card className="border-zinc-200/50 dark:border-zinc-800">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="ค้นหา Transfer ID, เลขบัญชี..."
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[160px]">
                <SelectValue placeholder="สถานะ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทั้งหมด</SelectItem>
                <SelectItem value="Approved">อนุมัติแล้ว</SelectItem>
                <SelectItem value="Pending">รอดำเนินการ</SelectItem>
                <SelectItem value="Rejected">ปฏิเสธ</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Upload From Filter */}
            <Select value={uploadFromFilter} onValueChange={setUploadFromFilter}>
              <SelectTrigger className="w-full sm:w-[140px]">
                <SelectValue placeholder="ช่องทาง" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทั้งหมด</SelectItem>
                <SelectItem value="Panel">Panel</SelectItem>
                <SelectItem value="Member">Member</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      {/* Table */}
      <Card className="border-zinc-200/50 dark:border-zinc-800 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50/80 dark:bg-zinc-900/50">
                <TableHead className="w-[50px]">#</TableHead>
                <TableHead className="w-[160px]">วันที่</TableHead>
                <TableHead className="w-[50px]">สลิป</TableHead>
                <TableHead className="w-[120px] text-right">จำนวนเงิน</TableHead>
                <TableHead className="w-[180px]">บัญชีโอน</TableHead>
                <TableHead className="w-[180px]">บัญชีรับ</TableHead>
                <TableHead className="w-[100px]">สถานะ</TableHead>
                <TableHead className="w-[80px]">ช่องทาง</TableHead>
                <TableHead className="w-[60px]">ซ้ำ</TableHead>
                <TableHead className="w-[200px]">ข้อความ</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            
            <TableBody>
              {isLoading ? (
                // Loading skeleton
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 11 }).map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-5 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : slips.length === 0 ? (
                // Empty state
                <TableRow>
                  <TableCell colSpan={11} className="h-48 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <FileImage className="w-12 h-12 text-muted-foreground/50" />
                      <div>
                        <p className="font-medium">ไม่พบรายการสลิป</p>
                        <p className="text-sm text-muted-foreground">
                          ลองปรับตัวกรองหรืออัปโหลดสลิปใหม่
                        </p>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                slips.map((slip, index) => {
                  const imageUrl = getSlipImageUrl(slip.imagePath)
                  const rowNumber = (page - 1) * pageSize + index + 1
                  
                  return (
                    <TableRow key={slip.id} className="hover:bg-zinc-50/50 dark:hover:bg-zinc-900/30">
                      {/* Row Number */}
                      <TableCell className="text-muted-foreground font-mono text-sm">
                        {rowNumber}
                      </TableCell>
                      
                      {/* DateTime */}
                      <TableCell className="font-medium text-sm">
                        {formatDateTime(slip.datetime || slip.date)}
                      </TableCell>
                      
                      {/* Slip Image */}
                      <TableCell>
                        {imageUrl ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => handleViewDetail(slip)}
                                  className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 transition-colors"
                                >
                                  <FileImage className="w-4 h-4" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>ดูรูปสลิป</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      
                      {/* Amount */}
                      <TableCell className="text-right">
                        <span className="font-bold text-lg text-emerald-600">
                          {formatCurrency(slip.amount)}
                        </span>
                      </TableCell>
                      
                      {/* Sender Bank */}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <BankLogo
                            bankValue={slip.senderBankId}
                            size="sm"
                          />
                          <div className="min-w-0">
                            <p className="font-mono text-sm truncate max-w-[110px]">
                              {slip.senderBankAccount || slip.fromAccount || '-'}
                            </p>
                            {slip.senderName && (
                              <p className="text-xs text-muted-foreground truncate max-w-[110px]">
                                {slip.senderName}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>

                      {/* Receiver Bank */}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <BankLogo
                            bankValue={slip.receiverBankId || slip.webBankName}
                            size="sm"
                          />
                          <div className="min-w-0">
                            <p className="font-mono text-sm truncate max-w-[110px]">
                              {slip.receiverBankAccount || slip.toAccount || slip.webBankAccount || '-'}
                            </p>
                            {(slip.receiverName || slip.webBankTag) && (
                              <p className="text-xs text-muted-foreground truncate max-w-[110px]">
                                {slip.receiverName || slip.webBankTag}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      
                      {/* Status */}
                      <TableCell>
                        <StatusBadge 
                          status={slip.status} 
                          verificationStatus={slip.verificationStatus} 
                        />
                      </TableCell>
                      
                      {/* Provider/Channel */}
                      <TableCell>
                        <ProviderBadge 
                          provider={slip.provider} 
                          uploadFrom={slip.uploadFrom} 
                        />
                      </TableCell>
                      
                      {/* Duplicate Check */}
                      <TableCell>
                        <DuplicateBadge 
                          slipCount={slip.slip_count} 
                          slipNew={slip.slip_new} 
                        />
                      </TableCell>
                      
                      {/* Message */}
                      <TableCell>
                        {slip.returnedMessage && (
                          <span className={cn(
                            "text-sm truncate block max-w-[180px]",
                            slip.returnedMessage.includes('สำเร็จ') || slip.returnedMessage.includes('เรียบร้อย')
                              ? 'text-emerald-600'
                              : slip.returnedMessage.includes('ซ้ำ') || slip.returnedMessage.includes('ไม่')
                              ? 'text-rose-600'
                              : 'text-amber-600'
                          )}>
                            {slip.returnedMessage}
                          </span>
                        )}
                      </TableCell>
                      
                      {/* Actions */}
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewDetail(slip)}>
                              <Eye className="w-4 h-4 mr-2" />
                              ดูรายละเอียดและรูปสลิป
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => navigator.clipboard.writeText(slip.transfer_id || '')}
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              คัดลอก Transfer ID
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </div>
        
        {/* Pagination */}
        {totalCount > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-200/50 dark:border-zinc-800">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>แสดง</span>
              <Select 
                value={pageSize.toString()} 
                onValueChange={(v) => {
                  setPageSize(Number(v))
                  setPage(1)
                }}
              >
                <SelectTrigger className="w-[70px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map((size) => (
                    <SelectItem key={size} value={size.toString()}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span>จาก {totalCount.toLocaleString()} รายการ</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <span className="text-sm text-muted-foreground px-2">
                หน้า {page} / {totalPages}
              </span>
              
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
      
      {/* Dialogs */}
      <UploadSlipDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        onSuccess={handleUploadSuccess}
      />
      
      <SlipDetailDialog
        slip={selectedSlip}
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
      />
    </div>
  )
}

export default SlipsPage
